import React, { useCallback, useEffect, useState } from 'react';
import { Row } from 'reactstrap';

import Pagination from 'components/Pagination';

import FilterContactsForm from './components/FilterContactsForm';
import ListContactsTable from './components/ListContactsTable';

import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { useListContacts } from '../../../hooks/useListContacts';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from '../../../../../../hooks/useMemorizeInputsFilters';
import { FlexTableSkeleton } from 'components/FlexTable/components/skeleton';
import extractPhoneNumber from './utils/extractPhoneNumber';

const ContactCRM = ({
  selectedRecordTypeId,
  setSelectedRecordType,
  recordTypes
}) => {
  const [activePage, setActivePage] = useState(1);
  const [filters, setFilters] = useState({});

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });

  const {
    getContacts,
    contacts,
    totalContacts,
    isLoadingContacts,
    clearContacts
  } = useListContacts({
    recordTypeId: selectedRecordTypeId,
    sortConfig
  });

  const { memorizeFilters, getMemorizedFilters } = useMemorizeFilters(
    POSSIBLE_FILTERS_ENTITIES.CRM_CONTACT
  );

  const { customFields, getCustomFields } = useEntityCustomFieldsByRecordType(
    entitiesName.CONTACT,
    selectedRecordTypeId
  );

  useEffect(() => {
    const currentFilters = {
      ...getMemorizedFilters(),
      ...filters,
      sort: sortConfig
    };
    handleSearch(currentFilters);
  }, [sortConfig]);

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    clearContacts();
  }, [selectedRecordTypeId, clearContacts]);

  const handleSearch = useCallback(
    (searchFilters) => {
      const page = searchFilters?.page || 1;
      setActivePage(page);
      if (searchFilters?.textQuery) {
        searchFilters.textQuery = extractPhoneNumber(searchFilters.textQuery);
      }
      setFilters(searchFilters);

      getContacts(page, searchFilters);
    },
    [getContacts]
  );

  const handlePageChange = (newPage) => {
    const currentFilters = getMemorizedFilters();
    memorizeFilters({ ...currentFilters, page: newPage });
    setActivePage(newPage);
    getContacts(newPage, filters);
  };

  const reloadContacts = useCallback(() => {
    getContacts(1, {});
  }, [getContacts]);

  return (
    <>
      <FilterContactsForm
        handleSearch={handleSearch}
        customFields={customFields}
        isLoadingContacts={isLoadingContacts}
        selectedRecordTypeId={selectedRecordTypeId}
        setSelectedRecordType={setSelectedRecordType}
        recordTypes={recordTypes}
        recordTypeId={selectedRecordTypeId}
        contacts={contacts}
        totalContacts={totalContacts}
        sortConfig={sortConfig}
      />

      <Row className="mt-4">
        {isLoadingContacts ? (
          <div className="col-12">
            <Row className="justify-content-md-center">
              <FlexTableSkeleton />
            </Row>
          </div>
        ) : (
          <ListContactsTable
            contacts={contacts}
            reloadContacts={reloadContacts}
            customFields={customFields}
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        )}
      </Row>
      <Pagination
        activePage={activePage}
        totalItemsCount={totalContacts}
        onChange={handlePageChange.bind(this)}
      />
    </>
  );
};

export default ContactCRM;
